'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Brand } from '@/components/ui/brand';
import { Icon } from '@/components/ui/icon';
import { buttonClasses } from '@/components/ui/button-classes';
import { cn } from '@/lib/utils';
import { LOGIN_HREF, SIGNUP_HREF } from './routes';

const LINKS = [
  { label: 'Fonctionnement', href: '#sec-fonctionnement' },
  { label: 'Sécurité', href: '#sec-securite' },
  { label: 'Tarifs', href: '#sec-tarifs' },
];

/**
 * Sous 861 px, la maquette masque `.tm-navlinks` (`display: none !important`)
 * sans rien y substituer — sur un petit téléphone, ni les ancres ni le bouton
 * « Connexion » ne restent atteignables, et logo + deux boutons côte à côte
 * débordent déjà à 375 px. On garde le CTA principal visible et on range le
 * reste (ancres + connexion) derrière un menu burger.
 */
export function LandingHeader() {
  const [open, setOpen] = useState(false);
  // Relief au scroll : le header se distingue du contenu passé sous lui au
  // lieu de rester à plat sur toute la page. `passive: true` + un seuil de
  // 8px (pas 0) évitent de retoggler la classe à chaque micro-scroll.
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={cn(
        'sticky top-0 z-20 border-b bg-white/88 backdrop-blur-[10px] transition-shadow duration-300',
        scrolled
          ? 'border-line shadow-[0_8px_24px_-18px_rgba(16,49,94,.45)]'
          : 'border-transparent',
      )}
    >
      <div className="mx-auto flex max-w-[1180px] items-center justify-between gap-3 px-4 py-[14px] nav:px-6">
        <Link href="/" aria-label="Accueil TonMonni" onClick={() => setOpen(false)}>
          <Brand size={32} />
        </Link>

        <nav className="hidden items-center gap-[30px] nav:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="relative text-[15px] font-medium text-ink transition-colors after:absolute after:-bottom-1 after:left-0 after:h-[2px] after:w-full after:origin-left after:scale-x-0 after:bg-green after:transition-transform after:duration-200 after:content-[''] hover:text-green hover:after:scale-x-100"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 nav:flex">
          <Link href={LOGIN_HREF} className={buttonClasses({ variant: 'ghost', sm: true })}>
            Connexion
          </Link>
          <Link href={SIGNUP_HREF} className={buttonClasses({ sm: true })}>
            Créer ma tontine
          </Link>
        </div>

        <div className="flex items-center gap-2 nav:hidden">
          <Link href={SIGNUP_HREF} className={buttonClasses({ sm: true })}>
            Créer ma tontine
          </Link>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={open}
            className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-[10px] border border-line bg-white text-navy"
          >
            <Icon name={open ? 'x' : 'menu'} size={20} />
          </button>
        </div>
      </div>

      {open ? (
        <div className="animate-tmfade border-t border-line bg-white px-4 py-3 nav:hidden">
          <nav className="flex flex-col gap-1">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="rounded-[10px] px-3 py-2.5 text-[15px] font-medium text-ink hover:bg-cream"
              >
                {l.label}
              </a>
            ))}
            <Link
              href={LOGIN_HREF}
              onClick={() => setOpen(false)}
              className="rounded-[10px] px-3 py-2.5 text-[15px] font-medium text-ink hover:bg-cream"
            >
              Connexion
            </Link>
          </nav>
        </div>
      ) : null}
    </header>
  );
}
